import { mbPlatformStyle } from '@mightybyte/rnw.utils.style-utils';
import React, { useState } from 'react';
import { Text, StyleSheet, ActivityIndicator, View, useWindowDimensions } from 'react-native';
import { BackButtonV2 } from '../../helperComponents/BackButtonV2';
import { ComponentWrapper } from '../../helperComponents/ComponentWrapper';
import File from 'react-native-vector-icons/FontAwesome';
import { useGetHiveByIdQuery } from '../MyHiveData/useGetHiveByIdQuery.gql';
import { ScreenProps } from '../../../typesInterfacesEnums/componentProps';
import { applyTransparency } from '../../../constants/colors';
import { GAME_TYPES_CONFIG, GAME_TYPES_TYPE, operandIcon } from '../../../constants/constants';
import { GameTypeCard } from '../../helperComponents/GameTypeCard';
import ChevronDown from 'react-native-vector-icons/FontAwesome';
import { SelectGamesPopUp } from '../../helperComponents/SelectGamesPopup';
import { useHiveUsersQuery } from '../MyHiveData/useHiveUsersQuery.gql';
import { useJourneysQuery } from './useJourneysQuery.gql';
import HiveReportsTable from '../../helperComponents/HiveReports/HiveReportsTable';

export default function HiveReports({ navigation, route }: ScreenProps<'HiveReports'>) {
  const { id } = route.params;
  const {
    data: { hive } = {},
    refetch: refetchHiveData,
    loading: hiveDataLoading,
  } = useGetHiveByIdQuery(id);

  const [selectedGameType, setSelectedGameType] = useState<GAME_TYPES_TYPE>(
    GAME_TYPES_TYPE.ADD_1_12,
  );
  const [showGamesModal, setShowGamesModal] = useState(false);

  // get journeys data
  const { data: journeysData, loading: journeysLoading } = useJourneysQuery(
    { hiveId: id, gameType: selectedGameType },
    { skip: !hive || !selectedGameType },
  );

  // get user details.
  const { data: getHivesData, loading } = useHiveUsersQuery({
    hiveId: id,
    page: 1,
    pageSize: 2000,
  });

  const { width } = useWindowDimensions();
  const sizeLimit = width < 800;

  // for loading
  if (!hive || hiveDataLoading) {
    return (
      <ComponentWrapper hideBee hasInset>
        <View style={{ justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <ActivityIndicator size={'large'} color={'E89823'} />
        </View>
      </ComponentWrapper>
    );
  }

  const OperandIconSelected = operandIcon[GAME_TYPES_CONFIG[selectedGameType].operation];

  const handleGameTypeDropdownPress = () => {
    setShowGamesModal(true);
  };

  const handleSelectGame = (game: GAME_TYPES_TYPE) => {
    setSelectedGameType(game);
    setShowGamesModal(false);
  };

  return (
    <>
      <ComponentWrapper
        hideBee
        hasInset
        scrollEnabled
        hasScroll={true}
        style={{ paddingHorizontal: 0 }}
      >
        <View style={styles.topContainer}>
          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <BackButtonV2
              onPress={() => {
                if (navigation.canGoBack()) {
                  navigation.goBack();
                } else {
                  navigation.navigate('MyHiveData', {
                    id,
                  });
                }
              }}
            />

            <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <Text style={styles.title}>
                <File name="cog" size={35} color="#E87030" /> Report: {hive.name}
              </Text>
            </View>
          </View>
          {!sizeLimit && (
            <View style={{ flexDirection: 'row', alignItems: 'center' }}>
              <View style={[styles.dropDownContainer]}>
                <GameTypeCard
                  onPress={handleGameTypeDropdownPress}
                  operandIcon={<OperandIconSelected />}
                  gameType={selectedGameType}
                  title={GAME_TYPES_CONFIG[selectedGameType].name}
                  disabled={false}
                />
                <ChevronDown name="chevron-down" size={14} style={styles.dropdownArrow} />
              </View>
            </View>
          )}
        </View>

        {sizeLimit && (
          <View style={[styles.dropDownContainer]}>
            <View
              style={{
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 20,
              }}
            >
              <View
                style={[
                  styles.dropDownContainer,
                  {
                    marginRight: 0,
                    marginBottom: 10,
                    justifyContent: 'center',
                    alignItems: 'center',
                    paddingHorizontal: 20,
                  },
                ]}
              >
                <GameTypeCard
                  onPress={handleGameTypeDropdownPress}
                  operandIcon={<OperandIconSelected />}
                  gameType={selectedGameType}
                  title={GAME_TYPES_CONFIG[selectedGameType].name}
                  disabled={false}
                />
                <ChevronDown
                  name="chevron-down"
                  size={14}
                  style={styles.dropdownArrowSmallScreen}
                />
              </View>
            </View>
          </View>
        )}

        <View style={{ marginTop: 30, width: '100%', alignItems: 'center' }}>
          {journeysData && getHivesData && (
            <HiveReportsTable
              hiveId={id}
              journeys={journeysData?.journeys}
              hiveUsers={getHivesData.hiveUsers}
            />
          )}
        </View>
      </ComponentWrapper>
      <SelectGamesPopUp
        visible={showGamesModal}
        setVisible={() => setShowGamesModal(false)}
        onSavePress={handleSelectGame}
        game={selectedGameType}
      />
    </>
  );
}

const styles = StyleSheet.create({
  topContainer: {
    zIndex: 100,
    height: 60,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    ...mbPlatformStyle({
      mobile: {
        paddingHorizontal: 20,
        marginTop: 0,
      },
      web: {
        paddingHorizontal: 60,
        marginTop: 60,
      },
    }),
  },
  title: {
    fontFamily: 'Secular One',
    fontSize: 28,
    textShadowColor: applyTransparency('#000000', 0.15),
    textShadowOffset: { width: 0, height: 4 },
    textShadowRadius: 2,
    color: '#E87030',
    marginHorizontal: 25,
  },
  dropDownContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 20,
    position: 'relative',
  },
  dropdownArrow: {
    marginLeft: 5,
    color: '#121212',
    position: 'absolute',
    right: 20,
  },
  dropdownArrowSmallScreen: {
    marginLeft: 5,
    color: '#121212',
    position: 'absolute',
    right: 40,
  },
});
